import React, { useState } from "react";
import { Linkedin, Mail } from "lucide-react";
import { QuoteDialog } from "@/components/QuoteDialog";

const TEAM = [
  {
    name: "Medha Chakraborty",
    role: "Founder",
    bio: "Building the AI-native recruiter. Product-led. Obsessed with fairness in hiring.",
    linkedin: "https://linkedin.com/in/medhachakraborty",
    initials: "MC",
    accent: "#06B6D4",
  },
  {
    name: "Sayantanee Chakraborty",
    role: "Co-founder",
    bio: "Systems thinker. Turning résumé chaos into a clean, ranked signal for hiring teams.",
    linkedin: "https://linkedin.com/in/sayantanee-chakraborty-2a81829",
    initials: "SC",
    accent: "#3B82F6",
  },
];

export const TeamSection = () => {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState("contact");

  const openOrMail = (e) => {
    // Desktop → open Quote dialog. Mobile (<md, 768px) → fall through to mailto:
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px)").matches) {
      e.preventDefault();
      setPlan("contact");
      setOpen(true);
    }
    // else: default anchor behavior fires mailto:
  };

  return (
    <section id="team" className="max-w-7xl mx-auto px-6 py-24 scroll-mt-20">
      <div className="mono-label">team</div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-1">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
          Small team. Big convictions about how hiring should work.
        </h2>
        <p className="text-slate-400 max-w-md">
          We've sat on both sides of the résumé pile. Axiony is the tool we always wished existed.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-4" data-testid="team-list">
        {TEAM.map((m) => (
          <div
            key={m.linkedin}
            data-testid={`team-member-${m.initials.toLowerCase()}`}
            className="group border border-white/10 bg-[#111827] rounded-2xl p-8 hover:border-white/20 hover:-translate-y-0.5 transition-transform flex gap-6 items-start"
          >
            <div
              className="w-16 h-16 shrink-0 rounded-2xl font-display font-semibold text-lg flex items-center justify-center border"
              style={{ background: `${m.accent}22`, color: m.accent, borderColor: `${m.accent}44` }}
            >
              {m.initials}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-display text-xl font-semibold text-slate-100">{m.name}</div>
                  <div className="mono-label mt-1" style={{ color: m.accent }}>{m.role}</div>
                </div>
                <a
                  href={m.linkedin}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`team-linkedin-${m.initials.toLowerCase()}`}
                  className="w-9 h-9 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 flex items-center justify-center transition-colors"
                  aria-label={`${m.name} on LinkedIn`}
                >
                  <Linkedin className="w-4 h-4 text-slate-300" />
                </a>
              </div>
              <p className="text-sm text-slate-400 mt-4 leading-relaxed">{m.bio}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 border border-white/10 bg-[#111827] rounded-xl p-6" data-testid="team-contact-block">
        <div className="mono-label text-[#06B6D4]">get in touch</div>
        <div className="flex-1 text-sm text-slate-300">
          Questions, partnerships, or want a walkthrough? Write to us directly.
        </div>
        <a
          href="mailto:medhachakraborty@axiony.co.in"
          onClick={openOrMail}
          data-testid="team-contact-email"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 transition-colors font-medium text-sm cursor-pointer"
        >
          <Mail className="w-4 h-4" />
          <span className="hidden md:inline">Get in touch</span>
          <span className="md:hidden">medhachakraborty@axiony.co.in</span>
        </a>
      </div>

      <QuoteDialog
        open={open}
        onOpenChange={setOpen}
        plan={plan}
        setPlan={setPlan}
        title="Get in touch"
        description="Drop us a note — we'll reply within one business day."
        messagePlaceholder="What would you like to chat about?"
      />
    </section>
  );
};
